<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useDark } from '@vueuse/core'
import { useI18n } from 'vue-i18n'

import IconNotification from '@/assets/svg/icon-bell-notification.svg'
import IconInfo from '@/assets/svg/IconInfo.svg'
import IconPhone from '@/assets/svg/icon-phone.svg'
import { useWebphonePermissions } from '@/composables/useWebphonePermissions'
import { getAppTimeZone } from '@/helpers/app-timezone-datetime.helper'
import { useAuthStore } from '@/stores/useAuthStore'
import { useNotificationStore } from '@/stores/useNotificationStore'

const authStore = useAuthStore()
const notificationStore = useNotificationStore()
const { t, locale } = useI18n()
const { shouldBlockWebphone, checkPermissions, tooltipKey } = useWebphonePermissions()
const isDark = useDark({ selector: 'html' })
const isScrolled = ref(false)
const isNotificationsOpen = ref(false)
const notificationsRef = ref<HTMLElement | null>(null)

const extensionNumber = computed(() => authStore.extension?.number || 'N/A')
const extensionDescription = computed(() => authStore.extension?.description || '-')
const isExtensionUnavailable = computed(() => !authStore.webphoneAvailable)
const isWebphoneDisabled = computed(() => shouldBlockWebphone.value || isExtensionUnavailable.value)
const webphoneTooltip = computed(() => t(tooltipKey.value))
const extensionStatusLabel = computed(() =>
  locale.value === 'en'
    ? isExtensionUnavailable.value
      ? 'Disconnected'
      : 'Connected'
    : isExtensionUnavailable.value
      ? 'Desconectada'
      : 'Conectada',
)
// const extensionNameWithStatus = computed(
//   () => `${extensionDescription.value} - ${extensionStatusLabel.value}`,
// )
const extensionNameWithStatus = computed(() => `${extensionDescription.value}`)
const currentDate = computed(() => {
  const currentLocale = locale.value === 'en' ? 'en-US' : 'es-ES'

  return new Intl.DateTimeFormat(currentLocale, {
    timeZone: getAppTimeZone(),
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date())
})

const notifications = computed(() =>
  notificationStore.notifications.filter((notification) => notification.type === 'incoming-call'),
)
const totalNotifications = computed(() => notifications.value.length)
const isIncomingCallRinging = computed(() => authStore.webphoneCallStatus === 'incoming')

const formatNotificationDate = (dateIso: string) => {
  const currentLocale = locale.value === 'en' ? 'en-US' : 'es-ES'
  const date = new Date(dateIso)
  return new Intl.DateTimeFormat(currentLocale, {
    timeZone: getAppTimeZone(),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

const handleScroll = () => {
  isScrolled.value = window.scrollY > 8
}

const toggleNotifications = () => {
  isNotificationsOpen.value = !isNotificationsOpen.value
}

const refreshWebphonePermissions = () => {
  void checkPermissions()
}

const handleWebphoneClick = () => {
  if (shouldBlockWebphone.value) return

  authStore.toggleWebphone()
}

const handleDocumentClick = (event: MouseEvent) => {
  if (!isNotificationsOpen.value) return

  const target = event.target as Node | null
  if (!target) return

  if (notificationsRef.value?.contains(target)) return
  isNotificationsOpen.value = false
}

onMounted(() => {
  refreshWebphonePermissions()
  handleScroll()
  window.addEventListener('scroll', handleScroll, { passive: true })
  window.addEventListener('focus', refreshWebphonePermissions)
  document.addEventListener('visibilitychange', refreshWebphonePermissions)
  document.addEventListener('click', handleDocumentClick)
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll)
  window.removeEventListener('focus', refreshWebphonePermissions)
  document.removeEventListener('visibilitychange', refreshWebphonePermissions)
  document.removeEventListener('click', handleDocumentClick)
})
</script>

<template>
  <div
    class="app-glass m-4 px-2 py-4 justify-between hidden border-2 border-white dark:border-[var(--p-surface-800)] lg:flex flex-row rounded-xl !shadow-sm"
    :class="[
      isDark ? 'app-glass--dark' : '',
      isScrolled
        ? 'shadow-lg border-b border-(--p-surface-200) dark:border-(--p-surface-700)'
        : 'shadow-none border-b border-transparent',
    ]"
  >
    <div class="flex items-center gap-3 pl-4">
      <div class="flex items-center gap-2">
        <div
          @click="handleWebphoneClick"
          class="relative transition-all duration-200 active:scale-95"
          :class="isWebphoneDisabled ? 'cursor-not-allowed' : 'cursor-pointer hover:scale-105'"
        >
          <IconPhone
            class="w-13 h-10"
            :class="[
              isWebphoneDisabled ? 'text-gray-400' : 'text-(--p-primary-color)',
              !isWebphoneDisabled && isIncomingCallRinging ? 'phone-ringing' : '',
            ]"
          />
          <span
            class="absolute inset-0 flex items-center ml-5 justify-center text-[11px] font-regular leading-none ibrand"
            :class="isWebphoneDisabled ? 'text-gray-400' : 'text-(--p-primary-color)'"
          >
            {{ extensionNumber }}
          </span>
        </div>

        <button
          v-if="shouldBlockWebphone"
          type="button"
          class="flex h-8 w-8 items-center justify-center rounded-full text-amber-400 transition hover:bg-amber-50 dark:hover:bg-amber-400/10"
          v-tooltip.bottom="webphoneTooltip"
          @mouseenter="refreshWebphonePermissions"
          @focus="refreshWebphonePermissions"
        >
          <IconInfo class="h-5 w-5" />
        </button>

        <!-- <span
          v-if="shouldBlockWebphone"
          class="text-[10px] leading-tight text-amber-600 dark:text-amber-400"
        >
          mic: {{ microphonePermission }} | snd: {{ soundPermission }}
        </span> -->
      </div>

      <div class="ml-2 flex flex-col leading-tight">
        <span class="quicksand font-bold text-sm text-(--p-primary-color)">
          {{ extensionNameWithStatus }}
        </span>
        <span class="montserrat text-xs text-(--p-primary-color) dark:text-(--p-surface-200)">
          {{ currentDate }}
        </span>
      </div>
    </div>

    <div ref="notificationsRef" class="relative flex items-center gap-2 mr-2">
      <button
        type="button"
        @click="toggleNotifications"
        class="app-glass flex h-10 w-10 items-center justify-center rounded-xl border-1 shadow-md border-(--p-primary-color)/10 text-(--p-primary-color) transition-all duration-200 hover:bg-(--p-primary-color)/10 cursor-pointer"
        aria-label="Notifications"
      >
        <IconNotification width="18" class="text-(--p-primary-color)" />
        <span
          v-if="totalNotifications > 0"
          class="absolute -top-1 -right-1 min-w-5 h-5 rounded-full bg-(--p-primary-color) text-white text-[10px] font-semibold flex items-center justify-center px-1"
        >
          {{ totalNotifications > 99 ? '99+' : totalNotifications }}
        </span>
      </button>

      <div
        v-if="isNotificationsOpen"
        class="absolute right-0 top-12 z-60 w-96 max-w-[92vw] rounded-xl border border-(--p-surface-200) bg-white shadow-xl dark:border-(--p-surface-700) dark:bg-(--p-surface-900)"
      >
        <div
          class="flex items-center justify-between px-4 py-3 border-b border-(--p-surface-200) dark:border-(--p-surface-700)"
        >
          <span class="text-sm font-semibold">
            {{ t('common.general.notifications') }}
          </span>
          <button
            v-if="notifications.length > 0"
            type="button"
            class="text-xs text-(--p-primary-color) hover:underline"
            @click="notificationStore.clearNotifications()"
          >
            {{ t('common.actions.clear') }}
          </button>
        </div>

        <div v-if="notifications.length === 0" class="px-4 py-6 text-sm text-(--p-surface-500)">
          {{ t('common.general.no_results') }}
        </div>

        <div v-else class="max-h-96 overflow-y-auto">
          <div
            v-for="item in notifications"
            :key="item.id"
            class="px-4 py-3 border-b border-(--p-surface-100) dark:border-(--p-surface-800)"
          >
            <div class="flex items-start justify-between gap-2">
              <div class="min-w-0">
                <p class="text-sm font-medium truncate">{{ item.title }}</p>
                <p class="text-xs text-(--p-surface-500) break-words">{{ item.message }}</p>
                <p
                  v-if="item.incomingCallDecision"
                  class="text-[11px] mt-1"
                  :class="
                    item.incomingCallDecision === 'accepted'
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-rose-600 dark:text-rose-400'
                  "
                >
                  {{ item.incomingCallDecision === 'accepted' ? 'Aceptada' : 'Rechazada' }}
                </p>
                <p class="text-[11px] text-(--p-surface-400) mt-1">
                  {{ formatNotificationDate(item.createdAt) }}
                </p>
              </div>
              <button
                type="button"
                class="text-xs text-(--p-primary-color) hover:underline"
                @click="notificationStore.removeNotification(item.id)"
              >
                {{ t('common.actions.delete') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@keyframes phone-ring {
  0% {
    transform: rotate(0deg);
  }
  15% {
    transform: rotate(-10deg);
  }
  30% {
    transform: rotate(10deg);
  }
  45% {
    transform: rotate(-10deg);
  }
  60% {
    transform: rotate(10deg);
  }
  75% {
    transform: rotate(-4deg);
  }
  100% {
    transform: rotate(0deg);
  }
}

.phone-ringing {
  transform-origin: 50% 85%;
  animation: phone-ring 0.8s ease-in-out infinite;
}
</style>
